import Msg from "./message";
import Format from "./format";
import FileServer from "./file-server";
import Field from "./field";
import Entity from "./entity";
import DocumentsCache from "./documents-cache";
import DocumentValue from "./document-value";
import Api from "./api";
import { isBase64String, template, uniq } from "../utils";
import IConfigDatetime from "../interface/field-cfg-datetime";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocumentsQuery from "../interface/documents-query";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import Message from "../enum/message";
import FieldType from "../enum/field-type";
import FieldSystemType from "../enum/field-system-type";
import ErrorCode from "../enum/error-code";
import DateType from "../enum/date-type";
import DocumentsQuery from "../class/documents-query";

class DocumentsSingleton {

    /* Public Methods */

    public async loadRelatedDocs(entity: IEntity, docs: IDocument[]): Promise<void> {
        const pointers: IField[] = Entity.filterFieldsByType(entity, [FieldType.pointer]);
        if (pointers.length) {
            const load: { [entityId: number]: number[] } = [];
            const data: { [entityId: number]: IDocument[] } = [];
            docs.forEach((doc: IDocument) => {
                pointers.forEach((p: IField) => {
                    const val: number | null = (doc[`c${p.id}`] as number[] || [])[0] || null;
                    const mod: IDocument | null = (doc[`d${p.id}`] as IDocument[] || [])[0] || null;
                    if (val && (!mod || val !== mod.id)) {
                        const entityId: number = p.pointer[1].ptEcn;
                        if (load[entityId]) {
                            load[entityId].push(val);
                        } else {
                            load[entityId] = [val];
                        }
                    }
                });
            });
            for (let prop in load) {
                const entityId: number = Number(prop);
                const ids: number[] = uniq(load[entityId]);
                data[entityId] = [];
                if (ids.length) {
                    data[entityId] = await Documents.loadDocuments(entityId, { ids });
                }
            }
            docs.forEach((doc: IDocument) => {
                pointers.forEach((p: IField) => {
                    const value: number[] = doc[`c${p.id}`] as number[];
                    const entityId: number = p.pointer[1].ptEcn;
                    if (!doc[`d${p.id}`]) {
                        doc[`d${p.id}`] = [];
                    }
                    if (data[entityId] && value instanceof Array && value.length) {
                        const rel: IDocument | null = data[entityId].find((d: IDocument) => {
                            return d.id === value[0];
                        }) || null;
                        if (rel) {
                            doc[`d${p.id}`] = [rel];
                        }
                    }
                });
            });
        }
    }

    public setLinkedFieldValues(entity: IEntity, docs: IDocument[]): void {
        /* Format only if we need to (skip for "text", "email", etc...) */
        const fields: IField[] = Entity.filterFieldsBySystemType(entity, FieldSystemType.linked);
        fields.forEach((field: IField) => {
            const entity: IEntity = Entity.get(field.foreignerEcn) as IEntity;
            const foreignFCNs: number[] = field.foreignerFCNs;
            const raw: boolean = field.uiType === FieldType.lookup && foreignFCNs.length < 2 && field.linkedTemplate === `<%= c${foreignFCNs[0]} %>`;
            docs.forEach((doc: IDocument) => {
                const pointerId: number = field.linkedRelation[0];
                const value: IDocument[] = DocumentValue.getPointer(doc[`d${pointerId}`]);
                let name: IDocumentValue = '';
                if (value.length) {
                    if (raw) {
                        name = value[0][`c${foreignFCNs[0]}`];
                    } else {
                        name = template(field.linkedTemplate, entity, foreignFCNs, value[0]);
                    }
                }
                doc[`d${field.id}`] = name;
            });
        });
    }

    public async create(entityId: number, data: IDocument = {}): Promise<IDocument> {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            return (await Api.insertDocuments(entityId, [this.validateDocument(entityId, data)]))[0];
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    public async update(doc: IDocument, entityId: number, fieldId: number, value: IDocumentValue): Promise<IDocument> {
        const field: IField | null = Field.get(fieldId);
        if (doc && field) {
            if (field.uiType === FieldType.linked) {
                if (value) {
                    doc[`c${field.linkedRelation[0]}`] = value;
                } else {
                    doc[`c${field.linkedRelation[0]}`] = [];
                }
            } else if (field.uiType === FieldType.image) {
                doc[`c${field.id}`] = await this.getImageValue(doc, entityId, fieldId, value as string);
            } else {
                doc[`c${field.id}`] = value;
            }
            return await Documents.replace(entityId, doc);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async replace(entityId: number, data: IDocument): Promise<IDocument> {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            await this.validateRecord(entityId, data);
            data = this.validateDocument(entityId, data);
            return (await Api.replaceDocument(entityId, data))[0];
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    public async prepare(entityId: number, data: IDocument): Promise<IDocument> {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            const imageFields: IField[] = Entity.getFieldsByType(entityId, [FieldType.image]);
            for (let i = 0; i < imageFields.length; i++) {
                const fieldId: number = imageFields[i].id;
                const value: IDocumentValue = data[`c${fieldId}`]
                if (value && typeof value === 'string') {
                    data[`c${fieldId}`] = await this.getImageValue(data, entityId, fieldId, value);
                }
            }
            return data;
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    public async delete(entityId: number, recordIds: number[]): Promise<number[]> {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            const result: IDocument[] = await Api.deleteMultipleDocuments(entity.id, recordIds);
            return result.map((doc: IDocument) => doc.id as number);
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    /* Private Public Methods */

    public async loadDocuments(entityId: number, q: IDocumentsQuery = {}, useCache: boolean = true): Promise<IDocument[]> {
        let query: DocumentsQuery | null = null;
        let options: string = '';
        q.noLF = true;
        query = new DocumentsQuery(q);
        options = query.toString();
        const cache: IDocument[] | null = DocumentsCache.get(entityId, options);
        if (useCache && cache) {
            return cache;
        } else {
            const result: IDocument[] = await Api.getAllDocuments(entityId, options);
            this.fixEmptyLinkedFieldValues(entityId, result);
            if (useCache && Entity.allowCache(entityId)) {
                DocumentsCache.add(entityId, result, query);
            }
            return result;
        }
    }

    public async loadOne(entityId: number, recordId: number): Promise<IDocument | null> {
        const records: IDocument[] = await Api.getOneDocument(entityId, recordId);
        if (records.length) {
            return records[0];
        } else {
            return null;
        }
    }

    public getTitle(doc: IDocument, entityId: number): string {
        const fieldId: number | null = Entity.getPrimaryFieldId(entityId);
        if (fieldId) {
            const title: string = Format.byFieldId(doc[`c${fieldId}`], fieldId);
            if (title) {
                return title;
            }
        }
        return `${Msg.get(Message.record)} ${doc.id}`;
    }

    public getEmpty(entity: IEntity): IDocument {
        const data: IDocument = {};
        entity.fields.forEach((field: IField) => {
            switch(field.uiType) {
                case FieldType.pointer:
                    data[`c${field.id}`] = [];
                    data[`d${field.id}`] = [];
                    break;
                case FieldType.linked:
                case FieldType.lookup:
                    data[`d${field.id}`] = '';
                    break;
                case FieldType.number:
                case FieldType.uid:
                    data[`c${field.id}`] = null;
                    break;
                default:
                    data[`c${field.id}`] = '';
                    break;
            }
        });
        return data;
    }

    /* Private Methods */

    private async validateRecord(entityId: number, data: IDocument): Promise<IDocument> {
        /* Creates a document if there was "Add record" selected for linked field */
        const fields: IField[] = Entity.getFieldsByType(entityId, [FieldType.linked]);
        for (let i = 0; i < fields.length; i++) {
            const field: IField = fields[i];
            const pointerId: number = field.linkedRelation[0];
            const value = data[`c${pointerId}`];
            if (typeof value === 'string') {
                const id: number = await this.addLinkedOption(field.id, value);
                data[`c${pointerId}`] = [id];
            }
        }
        return data;
    }

    private validateDocument(entityId: number, data: IDocument): IDocument {
        const result: IDocument = { id: data.id, _ts: data._ts, _tsi: data._tsi };
        Entity.getServerFields(entityId).forEach((field: IField) => {
            if (data[`c${field.id}`] || typeof data[`c${field.id}`] === 'number') {
                result[`c${field.id}`] = data[`c${field.id}`];
            }
            if (field.uiType === FieldType.datetime) {
                const cfg: IConfigDatetime = Field.getDatetimeConfig(field);
                const value: IDocumentValue = data[`c${field.id}`];
                if (cfg.type === DateType.DateOnly && value && typeof value === 'string') {
                    /* Fix date only value */
                    result[`c${field.id}`] = new Date(data[`c${field.id}`] as string).toLocaleDateString('sv')
                }
            }
        });
        return result;
    }

    private async addLinkedOption(fieldId: number, value: string): Promise<number> {
        const field: IField | null = Field.get(fieldId);
        if (value && field && field.uiType === FieldType.linked) {
            const record: IDocument = {};
            record['c' + field.foreignerFCNs[0]] = value.trim();
            const response: IDocument = await this.create(field.foreignerEcn, record);
            if (response) {
                return response.id as number;
            } else {
                throw ErrorCode.ApiInternal;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    private async getImageValue(doc: IDocument, entityId: number, fieldId: number, value: string): Promise<IDocumentValue> {
        const previous: IDocumentValue = doc[`c${fieldId}`];
        let result: string = `${value}`;
        if (isBase64String(value)) {
            /* If this is a base64 string, then save file to server and save it's name */
            const docId: number = doc.id as number || 0;
            result = await FileServer.saveImage(entityId, fieldId, docId, value);
        }
        if (previous && typeof previous === 'string' && isBase64String(previous)) {
            await FileServer.delete(previous);
        }
        return result;
    }

    private fixEmptyLinkedFieldValues(entityId: number, docs: IDocument[]): void {
        const linked: IField[] = Entity.getFieldsByType(entityId, [FieldType.linked]);
        if (linked.length) {
            docs.forEach((doc: IDocument) => {
                linked.forEach((field: IField) => {
                    if (doc[`c${field.id}`] === '\"\"') {
                        doc[`c${field.id}`] = '';
                    }
                })
            })
        }
    }

}

const Documents = new DocumentsSingleton();

export default Documents;